import React, { useEffect, useState } from "react";
import { Subheading } from "../../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../design/table";
import moment from "moment";
import VisitStatusBadge from "../../../components/VisitStatusBadge";
import CreateVisitButton from "../../../components/CreateVisitButton";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useAccount } from "../../../contexts/AccountContext";
import { useCase } from "../../../contexts/CaseContext";
import { Visit } from "@visitproof/types";
import { db } from "../../../firebase";

interface SupervisorDetailEventListProps {
  supervisorId: string;
}

const SupervisorDetailEventList: React.FC<SupervisorDetailEventListProps> = ({
  supervisorId,
}) => {
  const { account } = useAccount();
  const { cases } = useCase();
  const [visits, setVisits] = useState<Visit[]>([]);

  useEffect(() => {
    const docRef = query(
      collection(db, "visits"),
      where("accountId", "==", account.id),
      where("supervisorId", "==", supervisorId),
      orderBy("startDate", "desc"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        const visitList: Visit[] = [];
        docSnapshot.forEach((doc) => {
          const visit = doc.data() as Visit;
          visit.id = doc.id;
          visitList.push(visit);
        });
        setVisits(visitList);
      },
      (err) => {
        console.error("Error fetching visits for supervisor: ", err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [account.id, supervisorId]);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between w-full">
        <Subheading className="mt-12">Visits</Subheading>
        <div className="flex gap-4">
          <CreateVisitButton supervisorId={supervisorId} />
        </div>
      </div>
      {visits.length === 0 && (
        <div className="mt-8 text-center">
          <Subheading>No Visits found</Subheading>
        </div>
      )}
      {visits.length > 0 && (
        <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Case</TableHeader>
              <TableHeader>Start Date</TableHeader>
              <TableHeader>End Date</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.map((visit) => (
              <TableRow
                key={visit.id}
                title={`Visit #${visit.id}`}
                href={`/visit/${visit.id}`}
              >
                <TableCell>
                  {cases.find((c) => c.id === visit.caseId)?.identifier ?? "—"}
                </TableCell>
                <TableCell>
                  {moment(visit.startDate.toDate()).format("YYYY-MM-DD HH:mm")}
                </TableCell>
                <TableCell>
                  {moment(visit.endDate.toDate()).format("YYYY-MM-DD HH:mm")}
                </TableCell>
                <TableCell>
                  <VisitStatusBadge status={visit.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default SupervisorDetailEventList;
